import { useCallback, useMemo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';

import { AgreementSpaceReadModel, ProductReadModel, SetAgreementSpacePayload, SpaceReadModel, useGetPricesQuery } from '@client/api';
import {
  Button,
  CurrencyCode,
  HelperText,
  InfoTooltip,
  Input,
  TableCell,
  TableRow,
  Text,
  isDefinedString,
  setPriceWithProductPayloadSchema,
  toCurrencyStringOrUndefined,
  toPercent,
  useAreaMetricString,
} from '@client/toolkit';
import { InferType, ObjectSchema, object, string, boolean, number } from 'yup';

import { useTenantSettingsDefaults } from '../../../../../hooks';
import { AgreementSpaceProductSelect } from './agreement-space-product-select.component';
import { AgreementSpaceProductPriceSelect } from './agreement-space-product-price-select.component';
import { AgreementSpacesFormData } from './agreement-spaces-card.component';

type FormModel = {
  id?: string | null;
  spaceId: string;
  priceId: string;
  price: InferType<typeof setPriceWithProductPayloadSchema>;
  overwrittenPrice?: boolean | null;
  overrideArea?: number | null;
  overwrittenUnitAmount?: { amount?: number | null; currency?: { code?: string | null } | null } | null;
};

const schema: ObjectSchema<FormModel> = object({
  id: string().nullable(),
  spaceId: string().required(),
  priceId: string().required('Pris er påkrevd'),
  price: setPriceWithProductPayloadSchema,
  overwrittenPrice: boolean().nullable(),
  overrideArea: number().nullable(),
  overwrittenUnitAmount: object({
    amount: number().nullable(),
    currency: object({
      code: string().nullable(),
    }).nullable(),
  }).nullable(),
});

type FormValues = InferType<typeof schema>;

export const agreementSpaceSchema = schema;
export type AgreementSpaceFormData = FormValues;

interface AgreementSpaceItemFormProps {
  fieldIndex: number;
  agreementSpace?: AgreementSpaceFormData;
  space?: SpaceReadModel;
  allocation?: AgreementSpaceReadModel;
  isFetching?: boolean;
  isInvoiced?: boolean;
  onRemove?: (fieldIndex: number) => void;
  onStartEdit: () => void;
  onEndEdit: () => void;
  readOnly?: boolean;
}

export function AgreementSpaceItemForm({
  fieldIndex,
  agreementSpace,
  space,
  allocation,
  isFetching,
  isInvoiced,
  onRemove,
  onStartEdit,
  onEndEdit,
  readOnly = false,
}: AgreementSpaceItemFormProps) {
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');
  const { t: tValidation } = useTranslation('validation');
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<AgreementSpacesFormData>();

  const totalAreaString = useAreaMetricString(space?.area);
  const areaMetric = space?.area?.metric ?? 0;
  const commonAreaShareString = useAreaMetricString(
    allocation?.commonAreaShare != null ? { metric: allocation.commonAreaShare, imperial: 0 } : undefined,
  );
  const calculatedLettableAreaString = useAreaMetricString(
    allocation?.calculatedLettableArea != null ? { metric: allocation.calculatedLettableArea, imperial: 0 } : undefined,
  );
  const commonAreaAddOnFactorString =
    allocation?.commonAreaAddOnFactor != null ? toPercent(allocation.commonAreaAddOnFactor / 100) : undefined;
  const overrideAreaAddOnFactorString =
    allocation?.overrideAreaAddOnFactor != null ? toPercent(allocation.overrideAreaAddOnFactor / 100) : undefined;
  const {
    currentData: pricesData,
    isFetching: isFetchingPrices,
    refetch: refetchPrices,
  } = useGetPricesQuery({ 'pagination.Limit': 250 });
  const { defaultCurrencyCode } = useTenantSettingsDefaults();
  const selectedPrice = useWatch({
    control,
    name: `spaces.${fieldIndex}.price`,
  });
  const overrideArea = useWatch({
    control,
    name: `spaces.${fieldIndex}.overrideArea`,
  });
  const overwrittenPrice = useWatch({
    control,
    name: `spaces.${fieldIndex}.overwrittenPrice`,
  });

  const prices = useMemo(() => pricesData?.items ?? [], [pricesData?.items]);

  // Extract unique products from prices
  const products = useMemo(() => {
    const productMap = new Map<string, ProductReadModel>();
    prices.forEach((price) => {
      if (price.product && !productMap.has(price.product.id)) {
        productMap.set(price.product.id, price.product);
      }
    });
    return Array.from(productMap.values());
  }, [prices]);

  // Get currently selected product ID
  const selectedProductId = selectedPrice?.productId || '';

  // Filter prices by selected product
  const pricesForSelectedProduct = useMemo(() => {
    if (!selectedProductId) return [];
    return prices.filter((p) => p.product?.id === selectedProductId);
  }, [prices, selectedProductId]);

  const priceDetails = useMemo(() => {
    if (!selectedPrice?.id) return null;
    return prices.find((p) => p.id === selectedPrice.id) ?? null;
  }, [selectedPrice?.id, prices]);

  const priceError: string | undefined = useMemo(() => {
    if (errors?.spaces == null || errors?.spaces?.[fieldIndex] == null) return;
    return tValidation('property.required', { name: tGlobal('agreementSpace.price') });
  }, [errors?.spaces, fieldIndex, tGlobal, tValidation]);

  // Use overrideArea if set, otherwise use space area
  const effectiveAreaMetric = overrideArea ?? areaMetric;

  // Check if this is a one-time price (no interval)
  const isOneTimePrice = useMemo(() => {
    return !selectedPrice?.interval?.type;
  }, [selectedPrice?.interval?.type]);

  const totalCost = useMemo(() => {
    if (!selectedPrice?.unitAmount?.amount) return 0;

    const unitAmount = selectedPrice.unitAmount.amount;
    const intervalType = selectedPrice.interval?.type;
    const intervalCount = selectedPrice.interval?.count ?? 1;
    const hasUnit = priceDetails?.product?.unit != null;

    const baseAmount = hasUnit ? unitAmount * effectiveAreaMetric : unitAmount;

    switch (intervalType) {
      case 'Year':
        return baseAmount / intervalCount;
      case 'Month':
        return baseAmount * (12 / intervalCount);
      default:
        // One-time or no interval
        return baseAmount;
    }
  }, [
    selectedPrice?.unitAmount?.amount,
    selectedPrice?.interval?.type,
    selectedPrice?.interval?.count,
    priceDetails?.product?.unit,
    effectiveAreaMetric,
  ]);

  const handleUnitAmountChange = useCallback(
    (value: number) => {
      setValue(`spaces.${fieldIndex}.overwrittenPrice`, true, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`spaces.${fieldIndex}.price.unitAmount.amount`, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue, fieldIndex],
  );

  const handleOverrideAreaChange = useCallback(
    (value: number | null) => {
      setValue(`spaces.${fieldIndex}.overrideArea`, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue, fieldIndex],
  );

  const handleProductChange = useCallback(
    (productId: string) => {
      // When product changes, find the first price for that product and select it
      const firstPriceForProduct = prices.find((p) => p.product?.id === productId);
      if (firstPriceForProduct) {
        // Set priceId for API submission
        setValue(`spaces.${fieldIndex}.priceId`, firstPriceForProduct.id, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(
          `spaces.${fieldIndex}.price`,
          {
            id: firstPriceForProduct.id,
            productId: firstPriceForProduct.product.id,
            unitAmount: firstPriceForProduct.unitAmount,
            interval: firstPriceForProduct.interval || null,
            description: firstPriceForProduct.description || null,
            source: firstPriceForProduct.source || null,
          },
          { shouldDirty: true, shouldValidate: true },
        );
        // Reset overwritten price flag when changing product
        setValue(`spaces.${fieldIndex}.overwrittenPrice`, false, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [setValue, fieldIndex, prices],
  );

  const handlePriceChange = useCallback(
    (priceId: string) => {
      const selectedPriceData = prices.find((p) => p.id === priceId);
      if (selectedPriceData && selectedPriceData.product?.id) {
        // Set priceId for API submission
        setValue(`spaces.${fieldIndex}.priceId`, priceId, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(
          `spaces.${fieldIndex}.price`,
          {
            id: selectedPriceData.id,
            productId: selectedPriceData.product.id,
            unitAmount: selectedPriceData.unitAmount,
            interval: selectedPriceData.interval || null,
            description: selectedPriceData.description || null,
            source: selectedPriceData.source || null,
          },
          { shouldDirty: true, shouldValidate: true },
        );
        // Reset overwritten price flag when selecting a different price
        setValue(`spaces.${fieldIndex}.overwrittenPrice`, false, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    },
    [setValue, fieldIndex, prices],
  );

  useEffect(() => {
    refetchPrices();
  }, []);

  const spaceType = space?.discriminator ? tType(`SpaceDiscriminator.${space.discriminator}`) : undefined;

  return (
    <TableRow verticalAlign="middle">
      {/* Navn */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(space?.name)} className="flex items-center">
          <strong className="whitespace-nowrap font-medium">{space?.name}</strong>
        </Text>
      </TableCell>

      {/* Type */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(spaceType)}>
          {spaceType}
        </Text>
      </TableCell>

      {/* Areal */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(totalAreaString)}>
          {totalAreaString}
        </Text>
      </TableCell>

      {/* Andel fellesareal */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(commonAreaShareString)}>
          {commonAreaShareString || '-'}
        </Text>
      </TableCell>

      {/* Leieareal beregnet */}
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!isDefinedString(calculatedLettableAreaString)}>
          {calculatedLettableAreaString || '-'}
        </Text>
        {isDefinedString(commonAreaAddOnFactorString) && (
          <Text as="div" className="text-xs opacity-60">
            {tGlobal('agreementSpace.addOnFactor', { value: commonAreaAddOnFactorString })}
          </Text>
        )}
      </TableCell>

      {/* Leieareal avtale */}
      <TableCell loading={isFetching}>
        <Input
          type="number"
          size="medium"
          placeholder="-"
          value={overrideArea ?? ''}
          disabled={readOnly}
          onChange={(e) => {
            const value = e.target.value !== '' ? Number(e.target.value) : null;
            handleOverrideAreaChange(value);
          }}
        />
        {isDefinedString(overrideAreaAddOnFactorString) && (
          <Text as="div" className="text-xs opacity-60">
            {tGlobal('agreementSpace.addOnFactor', { value: overrideAreaAddOnFactorString })}
          </Text>
        )}
      </TableCell>

      {/* Produktnavn */}
      <TableCell loading={isFetchingPrices}>
        <AgreementSpaceProductSelect
          value={selectedProductId}
          products={products}
          onChange={handleProductChange}
          isLoading={isFetchingPrices}
          readOnly={readOnly}
        />
      </TableCell>

      {/* Produktpris */}
      <TableCell loading={isFetchingPrices}>
        <AgreementSpaceProductPriceSelect
          value={selectedPrice?.id || ''}
          prices={pricesForSelectedProduct}
          onChange={handlePriceChange}
          isLoading={isFetchingPrices}
          disabled={!selectedProductId || readOnly}
        />
      </TableCell>

      {/* Overstyrt leiepris */}
      <TableCell loading={isFetching}>
        <Input
          type="number"
          size="medium"
          placeholder="-"
          className="disabled:!border-neutral-800 disabled:!opacity-100"
          value={overwrittenPrice ? selectedPrice?.unitAmount?.amount ?? '' : ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              // Clear the override - reset to original price from the selected product price
              setValue(`spaces.${fieldIndex}.overwrittenPrice`, false, {
                shouldDirty: true,
                shouldValidate: true,
              });
              // Restore the original price amount from priceDetails
              if (priceDetails?.unitAmount?.amount != null) {
                setValue(`spaces.${fieldIndex}.price.unitAmount.amount`, priceDetails.unitAmount.amount, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            } else {
              handleUnitAmountChange(Number(value));
            }
          }}
          disabled={readOnly || !selectedPrice?.id}
        />
      </TableCell>

      {/* Fakturert */}
      <TableCell loading={isFetching}>
        {isOneTimePrice && isInvoiced != null ? (
          <Text as="div">{isInvoiced ? tGlobal('yes') : tGlobal('no')}</Text>
        ) : (
          <Text as="div">-</Text>
        )}
      </TableCell>

      {/* Totalt */}
      <TableCell align="right" loading={isFetching} className="flex items-center justify-end">
        <div className="text-primary">
          {priceError ? (
            <HelperText error>{priceError}</HelperText>
          ) : (
            tGlobal(isOneTimePrice ? 'agreementSpace.oneTimeCost' : 'agreementSpace.yearlyCost', {
              amount: toCurrencyStringOrUndefined(totalCost, undefined, {
                currency: defaultCurrencyCode as CurrencyCode,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            })
          )}
        </div>
        <InfoTooltip message="Kan ikke slettes pga. aktiv avtale" disabled={typeof onRemove === 'function'}>
          <Button
            className="ml-2 text-brand-primary bg-surface-elevated "
            size="xs"
            color="custom"
            disabled={typeof onRemove !== 'function' || isFetching}
            onClick={() => {
              if (typeof onRemove !== 'function') {
                return;
              }
              onEndEdit();
              onRemove(fieldIndex);
            }}
          >
            <FaRegTrashAlt />
          </Button>
        </InfoTooltip>
      </TableCell>
    </TableRow>
  );
}
